import React, { useRef, useState } from "react";
import CepInfoStep from "./steps/CepInfoStep";
import HospitalsListStep from "./steps/HospitalsListStep";
import PersonalInfoStep from "./steps/PersonalInfoStep";
import ProfessionalInfoStep from "./steps/ProfessionalInfoStep";
import PlanosInfoStep from "./steps/PlanosInfoStep";
import { createLead, createSolicitation } from "../services/api";

const CardWithLogo = ({ currentStep, handleNext, handleBack }) => {
  // --- ESTADOS DO COMPONENTE ---
  const personalRef = useRef(null);
  const professionalRef = useRef(null);

  const [cep, setCep] = useState("");
  const [personalData, setPersonalData] = useState(null);
  const [cepValid, setCepValid] = useState(false);
  const [hasHospitals, setHasHospitals] = useState(false);
  const [canProceedPersonal, setCanProceedPersonal] = useState(false);
  const [canProceedProfessional, setCanProceedProfessional] = useState(false);
  const [loading, setLoading] = useState(false);
  const totalSteps = 4;

  const onContinue = async (data) => {
    if (loading) return;

    // Etapa 1: Coleta de CEP
    if (currentStep === 1) {
      if (!cepValid || !hasHospitals) {
        alert("Informe um CEP válido e aguarde a busca por unidades.");
        return;
      }
      handleNext({ cep });
      return;
    }

    // Etapa 2: Coleta de Dados Pessoais
    if (currentStep === 2) {
      const getPersonal = personalRef.current?.getData;
      if (typeof getPersonal !== "function" || !canProceedPersonal) return;
      
      const personal = getPersonal();
      setPersonalData(personal);
      handleNext(personal);
      return;
    }

    // Etapa 3: Coleta de Dados Profissionais e Criação do Lead
    if (currentStep === 3) {
      const getProfessional = professionalRef.current?.getData;
      if (typeof getProfessional !== "function" || !canProceedProfessional) return;

      const professional = getProfessional();
      setLoading(true);
      try {
        const formData = {
          ...personalData,
          ...professional,
          zip_code: cep,
        };
        
        const response = await createLead(formData);

        // --- LÓGICA DE LEITURA DA RESPOSTA CORRIGIDA ---
        // A resposta do seu back-end Laravel contém o lead dentro de uma chave 'data'.
        const createdLead = response?.data?.data;

        // Verificamos se o lead e o seu ID existem antes de continuar.
        if (!createdLead || !createdLead.id) {
            console.error("❌ Erro: A resposta da API não contém um lead válido.", response);
            alert("Ocorreu um erro inesperado ao processar sua solicitação. Por favor, tente novamente.");
            setLoading(false);
            return;
        }

        localStorage.setItem("leadId", createdLead.id);
        
        console.log("✅ Lead processado com sucesso! ID:", createdLead.id);
        handleNext(professional);
        
      } catch (err) {
        console.error("❌ Erro ao criar lead =>", err);
        alert("Ops! Erro ao enviar seus dados. Verifique as informações e tente novamente.");
      } finally {
        setLoading(false);
      }
      return;
    }

    // Etapa 4: Seleção de Plano e Criação da Solicitação
    if (currentStep === 4) {
      const leadId = localStorage.getItem("leadId");
      const selectedPlan = data;

      if (!leadId || !selectedPlan?.id) {
        alert("Lead ou Plano inválido. Por favor, reinicie o processo.");
        return;
      }
      setLoading(true);
      try {
        const solicitationData = {
          id_leads: parseInt(leadId, 10),
          id_plans: selectedPlan.id,
        };
        
        await createSolicitation(solicitationData);
        
        localStorage.clear(); // Limpa todo o localStorage para uma nova sessão
        
        window.location.href = '#/obrigado';

      } catch (error) {
        console.error("❌ Erro ao criar solicitação final:", error);
        alert("Não foi possível concluir sua solicitação. Tente novamente mais tarde.");
      } finally {
        setLoading(false);
      }
      return;
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <>
            <CepInfoStep setCep={setCep} cepValid={cepValid} setCepValid={setCepValid} />
            {cepValid && <HospitalsListStep cep={cep} onCount={setHasHospitals} />}
          </>
        );
      case 2:
        return <PersonalInfoStep ref={personalRef} setCanProceed={setCanProceedPersonal} />;
      case 3:
        return <ProfessionalInfoStep ref={professionalRef} setCanProceed={setCanProceedProfessional} />;
      case 4:
        return <PlanosInfoStep onPlanSelect={onContinue} />;
      default:
        return null;
    }
  };
  // --- FIM DA LÓGICA DO COMPONENTE ---


  // --- INÍCIO DA RENDERIZAÇÃO VISUAL (JSX) ---
  return (
    <div className={`w-full ${currentStep === 4 ? "max-w-[98%]" : "max-w-[520px]"} flex flex-col items-center`}>
      <div className="mb-4">
        <span className="text-lg font-medium text-[#4B5563]">Etapa {currentStep} de {totalSteps}</span>
      </div>

      <div className="flex items-center mb-8">
        {[1, 2, 3, 4].map((step, idx) => (
          <React.Fragment key={step}>
            <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-white ${step < currentStep ? "bg-[#FFC700]" : step === currentStep ? "bg-[#2A4DD0]" : "bg-[#C0BEC1]"}`}>
              {step}
            </div>
            {idx < totalSteps - 1 && (<div className={`h-1 w-12 ${step < currentStep ? "bg-[#FFC700]" : "bg-[#C0BEC1]"}`} />)}
          </React.Fragment>
        ))}
      </div>

      {currentStep === 1 && (
        <>
          <h1 className="text-[24px] sm:text-[26px] font-bold text-[#224189] text-center leading-tight">
            Encontre o melhor <br /> plano de saúde para você
          </h1>
          <div className="flex w-full items-center justify-center space-x-4 my-8">
            <div className="border border-[#224189] rounded-lg p-4 text-center flex-1">
              <p className="text-base font-medium text-[#224189]">Planos Regionais a partir de</p>
              <p className="text-4xl font-extrabold text-[#224189] mt-1">R$129,90</p>
            </div>
            <div className="border border-[#224189] rounded-lg p-4 text-center flex-1">
              <p className="text-base font-medium text-[#224189]">Planos Nacionais a partir de</p>
              <p className="text-4xl font-extrabold text-[#224189] mt-1">R$249,11</p>
            </div>
          </div>
          <p className="text-base text-[#4B5563] text-center">Para começar, insira seu CEP abaixo:</p>
        </>
      )}

      {currentStep === 2 && (<h2 className="text-lg sm:text-xl font-medium text-[#4B5563] text-center mb-6">Agora insira suas informações pessoais</h2>)}
      {currentStep === 3 && (<h2 className="text-lg sm:text-xl font-medium text-[#4B5563] text-center mb-6">Para finalizar, informe seus dados profissionais:</h2>)}
      {currentStep === 4 && (<h2 className="text-lg sm:text-xl font-medium text-[#4B5563] text-center mb-6">Escolha seu plano</h2>)}

      <div className="w-full mt-6">{renderStep()}</div>

      {currentStep < 4 && (
        <div className="flex justify-between items-center w-full mt-16">
          <button onClick={() => currentStep === 1 ? (window.location.href = "http://alleanzabrasilcorretora.com.br/") : handleBack()} className="h-12 px-4 border border-[#2A4DD0] rounded-md text-[#2A4DD0] font-medium text-sm">
            ‹ Voltar
          </button>
          <button onClick={() => onContinue()} disabled={loading || (currentStep === 1 && (!cepValid || !hasHospitals)) || (currentStep === 2 && !canProceedPersonal) || (currentStep === 3 && !canProceedProfessional)} className={`h-12 px-6 rounded-md text-white font-medium text-sm ${loading || (currentStep === 1 && (!cepValid || !hasHospitals)) || (currentStep === 2 && !canProceedPersonal) || (currentStep === 3 && !canProceedProfessional) ? "bg-gray-400 cursor-not-allowed" : "bg-[#2A4DD0] hover:bg-blue-700"}`}>
            {loading ? "Processando..." : currentStep === 3 ? "Enviar Dados e Ver Planos ›" : "Continuar ›"}
          </button>
        </div>
      )}
    </div>
  );
};

export default CardWithLogo;
